import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import CardSection from '@shared/components/CardSection';
import { useConnectivityStore } from '@shared/hooks/useConnectivityStore';
import { useTriggerWeekly } from '../hooks/useTriggerWeekly';
import { useOperation } from '../hooks/useUpdates';
import type { UpdateOperation } from '../types';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface Props {
  lastWeeklyOp?: UpdateOperation;
}

export default function WeeklyUpdatePanel({ lastWeeklyOp }: Props) {
  const isOffline = useConnectivityStore((s) => s.state) !== 'online';
  const triggerWeekly = useTriggerWeekly();

  const activeOpId = triggerWeekly.data?.operationId ?? null;
  const { data: activeOp } = useOperation(activeOpId);

  const isActive = activeOp && (activeOp.status === 'queued' || activeOp.status === 'running');

  return (
    <div data-testid="weekly-update-panel" className="flex flex-col gap-3">
      <CardSection title="Weekly Update">
        {lastWeeklyOp && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Last run: {formatDate(lastWeeklyOp.startedAtUtc)}
            </Typography>
            <Chip
              label={lastWeeklyOp.uiStatus}
              size="small"
              color={lastWeeklyOp.status === 'succeeded' ? 'success' : 'error'}
              sx={{ mt: 0.5 }}
            />
          </Box>
        )}

        <Button
          variant="contained"
          onClick={() => triggerWeekly.mutate()}
          disabled={isOffline || triggerWeekly.isPending || !!isActive}
          startIcon={triggerWeekly.isPending ? <CircularProgress size={16} /> : undefined}
        >
          {triggerWeekly.isPending ? 'Triggering…' : 'Trigger Weekly Update'}
        </Button>

        {triggerWeekly.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {triggerWeekly.error?.message ?? 'Failed to trigger update'}
          </Alert>
        )}
      </CardSection>

      {isActive && activeOp && (
        <CardSection title="Update in Progress">
          <Typography variant="body2" sx={{ mb: 1 }}>
            Operation: {activeOp.operationId}
          </Typography>
          <LinearProgress variant="determinate" value={activeOp.progressPercent} sx={{ mb: 0.5 }} />
          <Typography variant="caption" color="text.secondary">
            {activeOp.progressPercent}% — {activeOp.status}
          </Typography>
        </CardSection>
      )}
    </div>
  );
}
